import React, { useState, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Search, ChevronDown, X } from 'lucide-react';
import { SPRITES, RARITIES, VARIANTS } from '../mock';

export default function SpritePicker({ value, onChange, excludeId, label = 'Pick a sprite' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const selected = useMemo(() => SPRITES.find((s) => s.id === value), [value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return SPRITES.filter((s) => {
      if (excludeId && s.id === excludeId) return false;
      if (!needle) return true;
      const blob = `${s.name} ${s.family} ${s.variant} ${s.rarity}`.toLowerCase();
      return blob.includes(needle);
    });
  }, [q, excludeId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-14 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white px-3 flex items-center gap-3 hover:bg-yellow-50 transition"
        >
          {selected ? (
            <>
              <img src={selected.img} alt="" className="h-9 w-9 object-contain shrink-0" />
              <div className="text-left min-w-0 flex-1">
                <div className="font-black text-sm truncate">{selected.name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: RARITIES[selected.rarity].color }}>
                    {RARITIES[selected.rarity].label}
                  </span>
                  <span className="text-[9px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: VARIANTS[selected.variant].color }}>
                    {VARIANTS[selected.variant].label}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(null); }}
                className="h-7 w-7 rounded-full bg-slate-100 hover:bg-rose-100 flex items-center justify-center text-slate-500 hover:text-rose-600 shrink-0"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <div className="h-9 w-9 rounded-lg bg-yellow-100 border-2 border-dashed border-[#3a1064]/40 flex items-center justify-center shrink-0">
                <Search className="h-4 w-4 text-[#3a1064]/60" />
              </div>
              <span className="flex-1 text-left text-[#3a1064]/70 font-bold text-sm">{label}</span>
              <ChevronDown className="h-4 w-4 text-[#3a1064]" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[440px] p-0 border-2 border-[#3a1064] rounded-xl shadow-[0_8px_0_#3a1064] overflow-hidden"
      >
        <div className="p-3 bg-[#fff7d6] border-b-2 border-[#3a1064]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search sprites by name, variant, rarity..."
              className="pl-9 h-10 rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064] bg-white"
            />
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto p-2 bg-white">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-[#3a1064] font-bold">No sprites found</div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {filtered.map((s) => {
                const r = RARITIES[s.rarity];
                const v = VARIANTS[s.variant];
                const isActive = value === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { onChange(s.id); setOpen(false); setQ(''); }}
                    className={`p-2 rounded-lg border-2 text-left transition-all hover:-translate-y-0.5 ${
                      isActive
                        ? 'border-[#3a1064] bg-yellow-100 shadow-[0_3px_0_#3a1064]'
                        : 'border-[#e1d0ff] bg-white hover:border-[#3a1064] hover:bg-yellow-50'
                    }`}
                  >
                    <div
                      className="aspect-square rounded-md flex items-center justify-center mb-1.5"
                      style={{ background: `linear-gradient(135deg, ${v.color}22, ${r.color}33)` }}
                    >
                      <img src={s.img} alt={s.name} className="h-[80%] w-[80%] object-contain" />
                    </div>
                    <div className="text-[11px] font-black text-[#3a1064] truncate leading-tight">{s.name}</div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: r.color }}>
                        {r.label}
                      </span>
                      <span className="text-[8px] font-black uppercase px-1 py-0.5 rounded text-white" style={{ background: v.color }}>
                        {v.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
